import { inferPopulation, type Patient, type PatientPopulation, type Sex } from '@ems/clinical';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

/** Patient context auto-clears after this long without changes so a weight never carries into the next call. */
export const PATIENT_TTL_MS = 90 * 60 * 1000;

export type WeightSource = 'stated' | 'measured' | 'length-based' | 'age-estimate';

interface PatientState {
  weightKg?: number;
  weightSource?: WeightSource;
  ageYears?: number;
  heightCm?: number;
  sex?: Sex;
  populationOverride?: PatientPopulation;
  updatedAt?: number;
  update: (patch: Partial<Omit<PatientState, 'update' | 'clear' | 'updatedAt'>>) => void;
  clear: () => void;
}

const EMPTY = {
  weightKg: undefined,
  weightSource: undefined,
  ageYears: undefined,
  heightCm: undefined,
  sex: undefined,
  populationOverride: undefined,
  updatedAt: undefined,
};

export const usePatientStore = create<PatientState>()(
  persist(
    (set) => ({
      ...EMPTY,
      update: (patch) => set({ ...patch, updatedAt: Date.now() }),
      clear: () => set({ ...EMPTY }),
    }),
    {
      name: 'ems-patient',
      // Session storage: patient context never survives closing the app.
      storage: createJSONStorage(() => sessionStorage),
      onRehydrateStorage: () => (state) => {
        if (state?.updatedAt && Date.now() - state.updatedAt > PATIENT_TTL_MS) state.clear();
      },
    },
  ),
);

export function usePatient(): Patient & { hasData: boolean; population?: PatientPopulation; weightSource?: WeightSource } {
  const s = usePatientStore();
  const patient: Patient = {
    weightKg: s.weightKg,
    ageYears: s.ageYears,
    heightCm: s.heightCm,
    sex: s.sex,
    population: s.populationOverride,
  };
  return {
    ...patient,
    population: inferPopulation(patient),
    weightSource: s.weightSource,
    hasData: s.weightKg !== undefined || s.ageYears !== undefined,
  };
}
